/**
 * 商品ギャラリーのサムネイル操作を補助する。
 *
 * サムネイルは横1行のスクロール表示にしているため、以下の2点を補う。
 * - 選択中のサムネイルが見切れていたら、表示領域内に寄せる
 * - PC のホイール操作で横スクロールできるようにする（横スクロールバーを隠しているため）
 */
(() => {
  const THUMBNAILS_SELECTOR = '.product-media-gallery__thumbnails-list';
  const BOUND_FLAG = 'thumbnailScrollBound';

  /**
   * 要素が横スクロール領域から外れていたら、最小限の移動で見える位置へ寄せる。
   * margin を与えると、その分だけ隣も見える位置まで動かす。
   * @param {HTMLElement} container
   * @param {Element} element
   * @param {number} [margin]
   */
  const scrollIntoViewX = (container, element, margin = 0) => {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (elementRect.left - margin < containerRect.left) {
      container.scrollBy({ left: elementRect.left - margin - containerRect.left, behavior: 'smooth' });
    } else if (elementRect.right + margin > containerRect.right) {
      container.scrollBy({ left: elementRect.right + margin - containerRect.right, behavior: 'smooth' });
    }
  };

  /**
   * 選択中のサムネイルを、その隣も見える位置までスクロールする。
   * @param {HTMLElement} list
   */
  const keepSelectedInView = (list) => {
    const selected = list.querySelector('[aria-selected="true"]');
    if (!selected) return;

    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    scrollIntoViewX(list, selected, selected.getBoundingClientRect().width + gap);
  };

  /**
   * アクティブなドットが一覧の外にあるとクリックできないため、見える位置へ寄せる。
   * @param {ParentNode} scope
   */
  const keepActiveDotInView = (scope) => {
    const container = scope.querySelector('.dots-pagination__dots');
    const active = scope.querySelector('.dots-pagination__dot[aria-selected="true"]');
    if (!(container instanceof HTMLElement) || !active) return;

    const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
    scrollIntoViewX(container, active.closest('li') || active, active.getBoundingClientRect().width + gap);
  };

  /**
   * 縦ホイールを横スクロールに変換する。トラックパッドの横スワイプはそのまま通す。
   * @param {HTMLElement} list
   */
  const enableWheelScroll = (list) => {
    list.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
        if (list.scrollWidth <= list.clientWidth) return;

        event.preventDefault();
        list.scrollLeft += event.deltaY;
      },
      { passive: false }
    );
  };

  /** @param {HTMLElement} list */
  const init = (list) => {
    if (list.dataset[BOUND_FLAG] === 'true') return;
    list.dataset[BOUND_FLAG] = 'true';

    enableWheelScroll(list);

    const observer = new MutationObserver(() => keepSelectedInView(list));
    observer.observe(list, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected'],
    });

    keepSelectedInView(list);
  };

  /*  CSS は [aria-selected='true'] で判定するため、toggleAttribute（空文字になる）は使えない  */
  /** @param {Element} el @param {boolean} selected */
  const setSelected = (el, selected) => {
    if (selected) {
      el.setAttribute('aria-selected', 'true');
    } else {
      el.removeAttribute('aria-selected');
    }
  };

  /**
   * クイック追加モーダル内のドット・サムネイルの選択状態を同期する。
   *
   * 商品ページでは product-media-gallery-content.liquid のインラインJSがこれを担うが、
   * モーダルは HTML を差し込む形なのでそのJSが再実行されない。
   *
   * slideshow-component は開くたびに作り直されるため、要素に直接購読すると
   * 差し替えでリスナーを失う。イベントは bubbles: true なので document で受ける。
   */
  const bindModalGallerySync = () => {
    document.addEventListener('slideshow:select', (event) => {
      const modal = document.getElementById('quick-add-modal-content');
      const target = event.target;

      if (!modal || !(target instanceof Node) || !modal.contains(target)) return;

      const detail = /** @type {CustomEvent} */ (event).detail;
      const index = Number(detail && detail.index);
      if (!Number.isInteger(index)) return;

      modal.querySelectorAll('.dots-pagination__dot').forEach((dot) => {
        if (!(dot instanceof HTMLElement)) return;
        setSelected(dot, Number(dot.dataset.slideIndex || '0') === index);
      });

      modal.querySelectorAll('.product-media-gallery__thumbnail').forEach((thumbnail, i) => {
        setSelected(thumbnail, i === index);
      });

      const list = modal.querySelector(THUMBNAILS_SELECTOR);
      if (list instanceof HTMLElement) keepSelectedInView(list);

      keepActiveDotInView(modal);
    });
  };

  /**
   * 「オプションを選択」はモーダルを開く前に商品ページを取得するため、
   * 押してから開くまでに間がある。その間ボタンを読み込み中の見た目にする。
   *
   * quick-add.js 側に状態管理がないため、クリックからダイアログが開くまでを
   * この場で見張る。
   */
  const bindQuickAddLoading = () => {
    const LOADING_CLASS = 'c_quick-add-loading';

    /** @type {HTMLElement | null} */
    let loadingButton = null;

    const clearLoading = () => {
      if (!loadingButton) return;
      loadingButton.classList.remove(LOADING_CLASS);
      loadingButton = null;
    };

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('.quick-add__button--choose');
      if (!(button instanceof HTMLElement)) return;

      clearLoading();
      loadingButton = button;
      button.classList.add(LOADING_CLASS);

      /*  取得に失敗してモーダルが開かない場合に、読み込み中のまま残らないようにする  */
      setTimeout(clearLoading, 8000);
    });

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent) {
      dialogComponent.addEventListener('dialog:open', clearLoading);
      dialogComponent.addEventListener('dialog:close', clearLoading);
    }
  };

  const initAll = () => {
    document.querySelectorAll(THUMBNAILS_SELECTOR).forEach((list) => {
      if (list instanceof HTMLElement) init(list);
    });
  };

  /**
   * モーダルは開くたびに中身が差し替わり、その際に監視対象の要素も作り直される。
   * 内容の変化を検知して初期化し直す。
   */
  const watchModal = () => {
    const modal = document.getElementById('quick-add-modal-content');
    if (!modal || modal.dataset.gallerySyncWatch === 'true') return;
    modal.dataset.gallerySyncWatch = 'true';

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        initAll();
      });
    });

    observer.observe(modal, { childList: true, subtree: true });
  };

  let syncBound = false;
  const bootstrap = () => {
    initAll();
    watchModal();

    if (!syncBound) {
      syncBound = true;
      bindModalGallerySync();
      bindQuickAddLoading();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }

  // テーマエディタでのセクション再描画に対応する
  document.addEventListener('shopify:section:load', bootstrap);
  document.addEventListener('section:loaded', bootstrap);
})();
